import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from } from 'rxjs';

import { AuthenticationService } from '../../auth/service';
import { switchMap, catchError } from 'rxjs/operators';
import { ConfigService } from '../../app-config.service';
import { fetchAuthSession } from 'aws-amplify/auth';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    /**
     *
     * @param {AuthenticationService} _authenticationService
     */
    constructor(
        private _authenticationService: AuthenticationService,
        private configService: ConfigService,
    ) {}

    /**
     * Add auth header with jwt if user is logged in and request is to api url
     * Token is retrieved directly from AWS Amplify sessionStorage (no duplication)
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const apiUrl = this.configService.getConfig.apiUrl;
        const isApiUrl = request.url.startsWith(apiUrl);
        // Backend revokes the current JWT via blacklist on this route; must send Bearer even if
        // isLoggedIn() is false (e.g. session edge cases) while Amplify still holds the session.
        const isLogoutRequest = isApiUrl && request.url.includes('/auth/logout');

        if (isLogoutRequest) {
            return from(fetchAuthSession()).pipe(
                switchMap((session) => {
                    const token = session.tokens?.idToken?.toString();
                    if (token) {
                        request = request.clone({
                            setHeaders: {
                                Authorization: `Bearer ${token}`,
                            },
                        });
                    }
                    return next.handle(request);
                }),
                catchError(() => next.handle(request)),
            );
        }

        return this._authenticationService.isLoggedIn(false).pipe(
            switchMap((loggedIn) => {
                if (loggedIn && isApiUrl) {
                    // Get JWT token directly from AWS Amplify (stored in sessionStorage)
                    return from(fetchAuthSession()).pipe(
                        switchMap((session) => {
                            const token = session.tokens?.idToken?.toString();

                            if (token) {
                                request = request.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${token}`,
                                    },
                                });
                            }

                            return next.handle(request);
                        }),
                        catchError(() => {
                            // If fetchAuthSession() fails, continue without token
                            return next.handle(request);
                        }),
                    );
                }

                return next.handle(request);
            }),
        );
    }
}
